import {
  createUnifiedTheme,
  palettes,
  genPageTheme,
  shapes,
} from '@backstage/theme';

// KG brand colors derived from kg-ui design system
const brand = {
  yellow: '#FFD400',
  darkYellow: '#D4AA00',
};

// Neutral palette from kg-ui (OKLch converted to hex)
const neutral = {
  50: '#FAFAFA',
  100: '#F5F5F5',
  200: '#E5E5E5',
  300: '#D4D4D4',
  400: '#A3A3A3',
  500: '#737373',
  600: '#525252',
  700: '#404040',
  800: '#262626',
  900: '#171717',
  950: '#0A0A0A',
};

// Page themes using neutral tones
const kgPageTheme = {
  home: genPageTheme({
    colors: [neutral[900]],
    shape: shapes.wave,
  }),
  documentation: genPageTheme({
    colors: [neutral[800], neutral[900]],
    shape: shapes.wave2,
  }),
  tool: genPageTheme({
    colors: [neutral[700], neutral[900]],
    shape: shapes.round,
  }),
  service: genPageTheme({
    colors: [neutral[900], neutral[800]],
    shape: shapes.wave,
  }),
  website: genPageTheme({
    colors: [neutral[800], neutral[700]],
    shape: shapes.wave,
  }),
  library: genPageTheme({
    colors: [neutral[900]],
    shape: shapes.wave,
  }),
  other: genPageTheme({
    colors: [neutral[700]],
    shape: shapes.wave,
  }),
  app: genPageTheme({
    colors: [neutral[800], neutral[900]],
    shape: shapes.wave,
  }),
  apis: genPageTheme({
    colors: [neutral[900], neutral[800]],
    shape: shapes.wave2,
  }),
  card: genPageTheme({
    colors: [neutral[800]],
    shape: shapes.wave,
  }),
};

export const kgLightTheme = createUnifiedTheme({
  palette: {
    ...palettes.light,
    primary: {
      main: neutral[900],
    },
    secondary: {
      main: brand.yellow,
    },
    background: {
      default: neutral[50],
      paper: '#FFFFFF',
    },
    border: neutral[200],
    textContrast: neutral[900],
    textVerySubtle: neutral[300],
    textSubtle: neutral[500],
    highlight: '#FFFBCC',
    errorBackground: '#FEF2F2',
    warningBackground: '#FFFBEB',
    infoBackground: '#EFF6FF',
    errorText: '#B91C1C',
    infoText: '#1D4ED8',
    warningText: neutral[900],
    link: neutral[900],
    linkHover: neutral[600],
    gold: brand.yellow,
    status: {
      ok: '#16A34A',
      warning: '#CA8A04',
      error: '#DC2626',
      running: '#2563EB',
      pending: brand.yellow,
      aborted: neutral[500],
    },
    banner: {
      info: '#2563EB',
      error: '#DC2626',
      text: '#FFFFFF',
      link: neutral[900],
      closeButtonColor: '#FFFFFF',
      warning: '#F59E0B',
    },
    navigation: {
      background: neutral[900],
      indicator: brand.yellow,
      color: neutral[400],
      selectedColor: '#FFFFFF',
      navItem: {
        hoverBackground: neutral[700],
      },
      submenu: {
        background: neutral[700],
      },
    },
    pinSidebarButton: {
      icon: neutral[900],
      background: neutral[300],
    },
    tabbar: {
      indicator: brand.yellow,
    },
  },
  fontFamily:
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  defaultPageTheme: 'home',
  pageTheme: kgPageTheme,
});

export const kgDarkTheme = createUnifiedTheme({
  palette: {
    ...palettes.dark,
    primary: {
      main: neutral[50],
      dark: neutral[200],
    },
    secondary: {
      main: brand.yellow,
    },
    background: {
      default: neutral[900],
      paper: neutral[800],
    },
    border: neutral[700],
    textContrast: neutral[50],
    textVerySubtle: neutral[600],
    textSubtle: neutral[400],
    highlight: '#422006',
    errorBackground: '#450A0A',
    warningBackground: '#451A03',
    infoBackground: '#172554',
    errorText: '#FCA5A5',
    infoText: '#93C5FD',
    warningText: '#FDE68A',
    link: neutral[50],
    linkHover: neutral[300],
    gold: brand.yellow,
    status: {
      ok: '#22C55E',
      warning: '#FACC15',
      error: '#EF4444',
      running: '#60A5FA',
      pending: brand.yellow,
      aborted: neutral[400],
    },
    banner: {
      info: '#3B82F6',
      error: '#EF4444',
      text: '#FFFFFF',
      link: neutral[50],
      closeButtonColor: '#FFFFFF',
      warning: '#F59E0B',
    },
    navigation: {
      background: neutral[950],
      indicator: brand.yellow,
      color: neutral[400],
      selectedColor: '#FFFFFF',
      navItem: {
        hoverBackground: neutral[700],
      },
      submenu: {
        background: neutral[700],
      },
    },
    pinSidebarButton: {
      icon: neutral[700],
      background: neutral[300],
    },
    tabbar: {
      indicator: brand.yellow,
    },
  },
  fontFamily:
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  defaultPageTheme: 'home',
  pageTheme: kgPageTheme,
});
